import React, { useMemo } from 'react';
import { AlertTriangle, CheckCircle2, ListChecks, TrendingUp } from 'lucide-react';
import { useAnalyticsOverview } from '../../hooks/useApi';
import { Distribution, PriorityChart } from '../../components/ReportCharts';
import { colorFor, humanize } from '../../components/charts/chartTheme';
import {
    Card,
    CardBody,
    CardHeader,
    ChartSkeleton,
    EmptyState,
    ProgressBar,
    StatCard,
    StatCardSkeleton,
} from '../../components/ui';

const OverviewTab = ({ filters }) => {
    const { data, isLoading } = useAnalyticsOverview(filters);

    const statusItems = useMemo(
        () =>
            (data?.status_distribution || []).map((d, i) => ({
                label: humanize(d.status),
                value: Number(d.count) || 0,
                color: colorFor(d.status, i),
            })),
        [data]
    );

    if (isLoading) {
        return (
            <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <StatCardSkeleton key={i} />
                    ))}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {Array.from({ length: 2 }).map((_, i) => (
                        <Card key={i}>
                            <CardBody>
                                <ChartSkeleton height={220} />
                            </CardBody>
                        </Card>
                    ))}
                </div>
            </div>
        );
    }

    if (!data?.stats) {
        return (
            <Card>
                <EmptyState
                    title="No data for this selection"
                    description="Widen the date range or clear a filter to see reporting figures."
                />
            </Card>
        );
    }

    const total = Number(data.stats.total_tasks) || 0;
    const completed = Number(data.stats.completed) || 0;
    const completionRate = total ? Math.round((completed / total) * 100) : 0;

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                <StatCard
                    label="Total tasks"
                    value={total}
                    icon={ListChecks}
                    tone="primary"
                    caption="In the selected period"
                />

                <Card className="p-4 min-h-[124px] flex flex-col">
                    <div className="flex items-start justify-between gap-3">
                        <p className="text-label font-medium text-ink-600 uppercase tracking-wide">
                            Completion rate
                        </p>
                        <CheckCircle2 size={17} className="text-success" aria-hidden="true" />
                    </div>
                    <p className="text-[26px] leading-none font-semibold text-ink-900 tabular mt-3">
                        {completionRate}%
                    </p>
                    <div className="mt-auto pt-3">
                        <ProgressBar value={completionRate} tone="var(--success)" />
                        <p className="text-label text-ink-500 mt-1.5 tabular">
                            {completed} of {total} complete
                        </p>
                    </div>
                </Card>

                <StatCard
                    label="Overdue tasks"
                    value={data.stats.overdue || 0}
                    icon={AlertTriangle}
                    tone="danger"
                    caption={
                        Number(data.stats.overdue) === 0 ? 'Nothing past due' : 'Needs attention'
                    }
                />

                <StatCard
                    label="Average progress"
                    value={`${Math.round(data.stats.avg_progress || 0)}%`}
                    icon={TrendingUp}
                    tone="info"
                    caption="Across active tasks"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card>
                    <CardHeader title="Task status" subtitle="Distribution across the period" />
                    <CardBody>
                        {statusItems.length ? (
                            <Distribution items={statusItems} centerLabel="Tasks" />
                        ) : (
                            <EmptyState
                                compact
                                title="No tasks in this period"
                                description="Adjust the date range to see the status breakdown."
                            />
                        )}
                    </CardBody>
                </Card>

                <Card>
                    <CardHeader title="Task priority" subtitle="Distribution across the period" />
                    <CardBody>
                        <PriorityChart data={data.priority_distribution} />
                    </CardBody>
                </Card>
            </div>
        </div>
    );
};

export default OverviewTab;
